import { unlockScroll, lockScroll } from '@archetype-themes/utils/a11y'
import { EVENTS } from '@archetype-themes/utils/events'

class CollectionMobileFilters extends HTMLElement {
  connectedCallback() {
    this.selectors = {
      filters: '.filter-wrapper',
      inlineWrapper: '#CollectionInlineFilterWrap',
      sortToggle: '[data-mobile-sort-toggle]',
      sortPanel: '.collection-mobile-sort',
      overlay: '[data-mobile-overlay]',
      filterBtn: '.collection-filter__sticky-btn--filter'
    }
    this.config = {
      mobileFiltersInPlace: false,
      isOpen: false,
      sortOpen: false,
      scrollLocked: false
    }

    this.mobileMediaQuery = window.matchMedia(`(max-width: 768px)`)
    this._filtersPromise = null

    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this)
    this.mobileMediaQuery.addEventListener('change', this.handleMediaQueryChange)
    this.handleMediaQueryChange(this.mobileMediaQuery)

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    this._toggleHandler = this.toggle.bind(this)
    this._closeAllHandler = this.handleCloseAll.bind(this)

    document.addEventListener(EVENTS.toggleMobileFilters, this._toggleHandler, { signal })
    document.addEventListener('filter:selected', this._closeAllHandler, { signal })
    document.addEventListener('collection:reloaded', this._closeAllHandler, { signal })

    this.filterRoot = this.closest('.collection-filter')
    this.mobileBar = this.closest('.collection-filter__mobile-bar')
    this.bindSortToggle(signal)
    this.bindFilterPrefetch(signal)

    const overlay = this.getOverlay()
    if (overlay) {
      overlay.addEventListener('click', this._closeAllHandler, { signal })
    }

    this._sortClickHandler = (evt) => {
      if (!this.isMobile()) {
        return
      }

      const sortBtn = evt.target.closest('.collection-mobile-sort__option')
      if (sortBtn) {
        this.handleSortButtonClick(evt)
      }
    }
    this.addEventListener('click', this._sortClickHandler, { signal })

    window.addEventListener(
      'keyup',
      (evt) => {
        if (evt.key === 'Escape' && (this.config.isOpen || this.config.sortOpen)) {
          this.closeAll()
        }
      },
      { signal }
    )
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  isMobile() {
    return this.mobileMediaQuery.matches
  }

  getSortToggle() {
    return this.mobileBar?.querySelector(this.selectors.sortToggle)
  }

  getOverlay() {
    return this.filterRoot?.querySelector(this.selectors.overlay)
  }

  setOverlayActive(isActive) {
    const overlay = this.getOverlay()

    if (isActive) {
      this.filterRoot?.classList.add('activeoverlayshadow')
      overlay?.setAttribute('aria-hidden', 'false')
    } else {
      this.filterRoot?.classList.remove('activeoverlayshadow')
      overlay?.setAttribute('aria-hidden', 'true')
    }
  }

  bindSortToggle(signal) {
    const sortToggle = this.getSortToggle()
    if (!sortToggle) {
      return
    }

    sortToggle.addEventListener(
      'click',
      () => {
        if (this.isMobile()) {
          this.toggleSort()
        }
      },
      { signal }
    )
  }

  bindFilterPrefetch(signal) {
    const filterBtn = this.mobileBar?.querySelector(this.selectors.filterBtn)
    if (!filterBtn) {
      return
    }

    filterBtn.addEventListener(
      'pointerdown',
      () => {
        if (this.isMobile()) {
          this.prefetchFilters()
        }
      },
      { signal, passive: true }
    )
  }

  handleCloseAll() {
    this._filtersPromise = null
    this.config.mobileFiltersInPlace = false
    this.closeAll()
  }

  schedulePrefetch() {
    const run = () => this.prefetchFilters()

    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 2500 })
    } else {
      setTimeout(run, 200)
    }
  }

  prefetchFilters() {
    if (!this.isMobile() || this.config.mobileFiltersInPlace) {
      return this._filtersPromise
    }

    if (!this._filtersPromise) {
      this._filtersPromise = this.renderFiltersOnMobile().catch(() => {
        this._filtersPromise = null
      })
    }

    return this._filtersPromise
  }

  async renderFiltersOnMobile() {
    if (!this.isMobile()) {
      return
    }

    if (this.config.mobileFiltersInPlace && this.querySelector(this.selectors.filters)) {
      return
    }

    const filters = await this.fetchFilters()
    const inlineWrapper = this.querySelector(this.selectors.inlineWrapper)

    if (!filters || !inlineWrapper) {
      return
    }

    inlineWrapper.replaceChildren(filters)
    this.config.mobileFiltersInPlace = true
  }

  handleSortButtonClick(evt) {
    const btn = evt.currentTarget
    const sortValue = btn.dataset.value

    this.closeAll()

    requestAnimationFrame(() => {
      this.dispatchEvent(new CustomEvent(EVENTS.sortSelected, { detail: { sortValue }, bubbles: true }))
    })
  }

  handleMediaQueryChange(mql) {
    if (mql.matches) {
      this.schedulePrefetch()
      return
    }

    this.closeAll()
    this._filtersPromise = null
    this.config.mobileFiltersInPlace = false
  }

  toggleSort() {
    if (!this.isMobile()) {
      return
    }

    if (this.config.sortOpen) {
      this.closeSort()
    } else {
      this.close()
      this.openSort()
    }
  }

  openSort() {
    const sortPanel = this.querySelector(this.selectors.sortPanel)
    if (!sortPanel) {
      return
    }

    sortPanel.classList.add('is-active')
    sortPanel.setAttribute('aria-hidden', 'false')
    this.getSortToggle()?.setAttribute('aria-expanded', 'true')
    this.getSortToggle()?.classList.add('is-active')
    this.setOverlayActive(true)
    this.config.sortOpen = true

    this.deferHeavyUiWork(true)
  }

  closeSort() {
    if (!this.config.sortOpen) {
      return
    }

    const sortPanel = this.querySelector(this.selectors.sortPanel)
    if (sortPanel) {
      sortPanel.classList.remove('is-active')
      sortPanel.setAttribute('aria-hidden', 'true')
    }

    this.getSortToggle()?.setAttribute('aria-expanded', 'false')
    this.getSortToggle()?.classList.remove('is-active')
    this.config.sortOpen = false

    if (!this.config.isOpen) {
      this.setOverlayActive(false)
      this.deferHeavyUiWork(false)
    }
  }

  toggle(evt) {
    if (!this.isMobile()) {
      return
    }

    const shouldOpen = typeof evt?.detail?.isOpen === 'boolean' ? evt.detail.isOpen : !this.config.isOpen

    if (shouldOpen) {
      this.open()
    } else {
      this.close()
    }
  }

  async open() {
    if (!this.isMobile()) {
      return
    }

    this.setOverlayActive(true)
    this.config.isOpen = true
    this.deferHeavyUiWork(true)

    await this.prefetchFilters()

    const filters = this.querySelector(this.selectors.filters)
    if (!filters) {
      this.config.isOpen = false
      this.setOverlayActive(false)
      this.deferHeavyUiWork(false)
      document.dispatchEvent(new CustomEvent('mobileFilters:closed'))
      return
    }

    this.closeSort()
    filters.classList.add('is-active')
  }

  close() {
    if (!this.config.isOpen) {
      return
    }

    const filters = this.querySelector(this.selectors.filters)

    if (filters) {
      filters.classList.remove('is-active')
    }

    this.config.isOpen = false

    if (!this.config.sortOpen) {
      this.setOverlayActive(false)
      this.deferHeavyUiWork(false)
    }
  }

  closeAll() {
    const wasOpen = this.config.isOpen || this.config.sortOpen
    this.close()
    this.closeSort()

    if (wasOpen) {
      document.dispatchEvent(new CustomEvent('mobileFilters:closed'))
    }
  }

  deferHeavyUiWork(isOpen) {
    requestAnimationFrame(() => {
      if (isOpen) {
        this.lockPageScroll()
      } else {
        this.unlockPageScroll()
      }

      this.dispatchDrawerSizeEvent(isOpen)
    })
  }

  lockPageScroll() {
    if (!this.config.scrollLocked) {
      lockScroll()
      this.config.scrollLocked = true
    }
  }

  unlockPageScroll() {
    if (this.config.scrollLocked) {
      unlockScroll()
      this.config.scrollLocked = false
    }
  }

  dispatchDrawerSizeEvent(isOpen) {
    const filterBar = this.closest('.collection-filter')

    document.dispatchEvent(
      new CustomEvent(EVENTS.sizeDrawer, {
        detail: isOpen && filterBar ? { heights: [filterBar.offsetHeight] } : undefined,
        bubbles: true
      })
    )
  }

  async fetchFilters() {
    const searchParams = window.location.search.slice(1)
    const url = `${window.location.pathname}?section_id=item-grid-filters&${searchParams}`
    const response = await fetch(url)

    if (!response.ok) {
      throw response
    }

    const responseText = await response.text()
    const html = new DOMParser().parseFromString(responseText, 'text/html')
    return html.querySelector(this.selectors.filters)
  }
}

customElements.define('collection-mobile-filters', CollectionMobileFilters)
